import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Users } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const PAGE = 50;

/**
 * Admin → Patients: the register behind the Patients count on the control room.
 * Every person who has an account, with what they have bought and booked, worked out
 * from the orders and appointments the panel has already loaded.
 */
export const PatientsTab = ({ users = [], orders = [], appointments = [] }) => {
  const [q, setQ] = useState("");
  const [shown, setShown] = useState(PAGE);

  const rows = useMemo(() => {
    const byUser = (list) => list.reduce((acc, row) => {
      const key = row.user_id || row.patient_id;
      if (key) (acc[key] = acc[key] || []).push(row);
      return acc;
    }, {});
    const orderMap = byUser(orders);
    const apptMap = byUser(appointments);
    const needle = q.trim().toLowerCase();
    return users
      .map((u) => {
        const mine = orderMap[u.id] || [];
        return {
          ...u,
          orders: mine.length,
          spend: mine.filter((o) => o.payment_status === "paid")
            .reduce((sum, o) => sum + Number(o.total || 0), 0),
          appointments: (apptMap[u.id] || []).length,
        };
      })
      .filter((u) => !needle || [u.name, u.email, u.phone]
        .some((v) => (v || "").toLowerCase().includes(needle)))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [users, orders, appointments, q]);

  return (
    <Card className="border-border" data-testid="patients-tab">
      <CardContent className="p-6 lg:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h3 className="font-display text-2xl flex items-center gap-2"><Users size={18} /> Patient register</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Everyone with an account, newest first — with their orders, spend and consultations.
            </p>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setShown(PAGE); }}
              placeholder="Search name, email or phone"
              className="pl-9 w-72 bg-transparent"
              data-testid="patients-search"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="patients-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-3 pr-4 font-normal">Patient</th>
                <th className="py-3 pr-4 font-normal">Contact</th>
                <th className="py-3 pr-4 font-normal">Joined</th>
                <th className="py-3 pr-4 font-normal">Orders</th>
                <th className="py-3 pr-4 font-normal">Spend</th>
                <th className="py-3 font-normal">Consultations</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, shown).map((u) => (
                <tr key={u.id} className="border-t border-border/70 hover:bg-muted/40 transition-colors" data-testid={`patient-row-${u.id}`}>
                  <td className="py-3 pr-4">
                    <div className="font-medium">{u.name || "—"}</div>
                    {u.age ? <div className="text-xs text-muted-foreground">Age {u.age}</div> : null}
                  </td>
                  <td className="py-3 pr-4">
                    <div>{u.email}</div>
                    <div className="text-xs text-muted-foreground">{u.phone || "No phone"}</div>
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap">{day(u.created_at)}</td>
                  <td className="py-3 pr-4">{u.orders}</td>
                  <td className="py-3 pr-4 whitespace-nowrap">{money(u.spend)}</td>
                  <td className="py-3">
                    {u.appointments > 0
                      ? <Badge variant="outline" className="text-[10px]">{u.appointments}</Badge>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground py-6" data-testid="patients-empty">
              {users.length === 0 ? "No patient accounts yet." : "No patient matches that search."}
            </p>
          )}
          {rows.length > shown && (
            <div className="flex items-center gap-4 pt-5">
              <Button
                variant="outline"
                onClick={() => setShown(shown + PAGE)}
                className="btn-pill border-border"
                data-testid="patients-load-more"
              >
                Show 50 more
              </Button>
              <span className="text-xs text-muted-foreground" data-testid="patients-count">
                Showing {shown} of {rows.length}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PatientsTab;
